/**
 * AdminHustleMeetSettingsPage.jsx — admin dial dashboard for HustleMeet.
 *
 * Reads/writes:
 *   GET  /api/admin/hustlemeet-settings
 *   POST /api/admin/hustlemeet-settings   (partial patch)
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Save, RotateCcw, Sliders, AlertTriangle, FileDown, Sparkles } from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const authHeader = () => {
  const t = localStorage.getItem("token") || localStorage.getItem("access_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

function Number_({ label, value, onChange, testId, min, max, step = 0.5, suffix }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest text-stone-500 font-bold">{label}</span>
      <div className="relative mt-1">
        <input
          data-testid={testId}
          type="number" min={min} max={max} step={step}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
          className="w-full h-11 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-slate-900 px-3 pr-10 text-sm"
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-stone-500">
            {suffix}
          </span>
        )}
      </div>
    </label>
  );
}

function Select_({ label, value, onChange, options, testId }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest text-stone-500 font-bold">{label}</span>
      <select
        data-testid={testId}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full h-11 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-slate-900 px-3 text-sm"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

export default function AdminHustleMeetSettingsPage() {
  const [orig,  setOrig]  = useState(null);
  const [draft, setDraft] = useState(null);
  const [err,   setErr]   = useState("");
  const [busy,  setBusy]  = useState(false);

  useEffect(() => {
    document.title = "HustleMeet · Agency Settings";
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/admin/hustlemeet-settings`,
          { headers: authHeader(), withCredentials: true });
        setOrig(data);
        setDraft(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || e.message);
      }
    })();
  }, []);

  const dirty = orig && draft && JSON.stringify(orig) !== JSON.stringify(draft);

  const save = async () => {
    if (!draft) return;
    // Send only the writable fields.
    const patch = {
      platform_fee_pct:            draft.platform_fee_pct,
      member_rewards_pct:          draft.member_rewards_pct,
      hustle_share_pct:            draft.hustle_share_pct,
      credit_expiry_years:         draft.credit_expiry_years,
      cancel_windows:              draft.cancel_windows,
      min_confidence_threshold:    draft.min_confidence_threshold,
      invite_rate_limit_per_14d:   draft.invite_rate_limit_per_14d,
      matchmaker_autonomy:         draft.matchmaker_autonomy,
      default_feast_select_mode:   draft.default_feast_select_mode,
      reliability_pause_below_pct: draft.reliability_pause_below_pct,
      referrer_credit_cents:       draft.referrer_credit_cents,
      referee_credit_cents:        draft.referee_credit_cents,
      host_payout_pct:             draft.host_payout_pct,
    };
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/hustlemeet-settings`, patch,
        { headers: authHeader(), withCredentials: true });
      setOrig(data);
      setDraft(data);
      toast.success("Settings saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  if (err) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#0a0908] flex items-center justify-center px-6">
        <div className="max-w-md text-center" data-testid="settings-error">
          <p className="text-lg font-black">Access denied</p>
          <p className="mt-2 text-stone-500 text-sm">{err}</p>
        </div>
      </div>
    );
  }
  if (!draft) {
    return (
      <div data-testid="settings-loading" className="min-h-screen bg-slate-50 dark:bg-[#0a0908] flex items-center justify-center text-stone-400">
        Loading…
      </div>
    );
  }

  const splitSum = Number(draft.member_rewards_pct || 0) + Number(draft.hustle_share_pct || 0);
  const splitOK  = splitSum <= Number(draft.platform_fee_pct || 0) + 0.001;

  return (
    <div
      data-testid="admin-hustlemeet-settings-page"
      className="min-h-screen bg-slate-50 text-stone-900 dark:bg-[#0a0908] dark:text-stone-100 transition-colors"
    >
      <PlatformHeader
        brand={{ name: "Admin · HustleMeet Settings", to: "/admin/hustlemeet-settings" }}
        showBookCta={false} showCart={false}
      />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-24">
        <header className="mb-8 flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-amber-500/10 text-amber-500 flex items-center justify-center">
            <Sliders size={22}/>
          </div>
          <div className="flex-1">
            <p className="text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">
              Agency dials
            </p>
            <h1 className="mt-1 text-2xl md:text-3xl font-black">HustleMeet settings</h1>
            <p className="mt-1 text-xs text-stone-500">
              Split, cancel windows, AI autonomy & rewards. Live for every future booking.
            </p>
          </div>
        </header>

        {/* Fees & split */}
        <section className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-6 mb-4">
          <h2 className="font-black mb-4">Platform fee & split</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Number_ label="Platform fee" suffix="%" testId="input-platform-fee-pct"
              value={draft.platform_fee_pct}
              onChange={(v) => setDraft({ ...draft, platform_fee_pct: v })}
              min={0} max={30} step={0.5}/>
            <Number_ label="Member rewards" suffix="%" testId="input-member-rewards-pct"
              value={draft.member_rewards_pct}
              onChange={(v) => setDraft({ ...draft, member_rewards_pct: v })}
              min={0} max={30} step={0.5}/>
            <Number_ label="Hustle share" suffix="%" testId="input-hustle-share-pct"
              value={draft.hustle_share_pct}
              onChange={(v) => setDraft({ ...draft, hustle_share_pct: v })}
              min={0} max={30} step={0.5}/>
          </div>
          {!splitOK && (
            <div className="mt-3 flex items-start gap-2 text-red-500 text-xs" data-testid="split-warning">
              <AlertTriangle size={14} className="mt-0.5"/>
              <span>member + hustle ({splitSum}%) cannot exceed platform fee ({draft.platform_fee_pct}%).</span>
            </div>
          )}
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Number_ label="Credit expiry" suffix="years" testId="input-credit-expiry-years"
              value={draft.credit_expiry_years}
              onChange={(v) => setDraft({ ...draft, credit_expiry_years: v })}
              min={1} max={20} step={1}/>
            <Number_ label="Host payout" suffix="%" testId="input-host-payout-pct"
              value={draft.host_payout_pct}
              onChange={(v) => setDraft({ ...draft, host_payout_pct: v })}
              min={0} max={30} step={0.5}/>
          </div>
        </section>

        {/* Cancel windows */}
        <section className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-6 mb-4">
          <h2 className="font-black mb-4">Cancellation windows</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Number_ label="100% refund up to" suffix="hrs" testId="input-full-refund-hrs"
              value={draft.cancel_windows?.full_refund_hrs}
              onChange={(v) => setDraft({ ...draft, cancel_windows: { ...draft.cancel_windows, full_refund_hrs: v } })}
              min={0} max={168} step={1}/>
            <Number_ label="50% refund up to" suffix="hrs" testId="input-half-refund-hrs"
              value={draft.cancel_windows?.half_refund_hrs}
              onChange={(v) => setDraft({ ...draft, cancel_windows: { ...draft.cancel_windows, half_refund_hrs: v } })}
              min={0} max={168} step={1}/>
            <Number_ label="0% refund under" suffix="hrs" testId="input-no-refund-hrs"
              value={draft.cancel_windows?.no_refund_hrs}
              onChange={(v) => setDraft({ ...draft, cancel_windows: { ...draft.cancel_windows, no_refund_hrs: v } })}
              min={0} max={168} step={1}/>
          </div>
        </section>

        {/* AI Matchmaker */}
        <section className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-6 mb-4">
          <h2 className="font-black mb-4">Matchmaker AI</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Select_ label="Autonomy" testId="input-matchmaker-autonomy"
              value={draft.matchmaker_autonomy}
              options={[
                { value: "full_auto",       label: "Full auto (no admin review)" },
                { value: "admin_approve",   label: "Admin approves before send" },
              ]}
              onChange={(v) => setDraft({ ...draft, matchmaker_autonomy: v })}
            />
            <Number_ label="Confidence threshold" suffix="0-1" testId="input-min-conf"
              value={draft.min_confidence_threshold}
              onChange={(v) => setDraft({ ...draft, min_confidence_threshold: v })}
              min={0} max={1} step={0.05}/>
            <Number_ label="Invite rate limit" suffix="/ 14d" testId="input-invite-rate"
              value={draft.invite_rate_limit_per_14d}
              onChange={(v) => setDraft({ ...draft, invite_rate_limit_per_14d: v })}
              min={1} max={20} step={1}/>
            <Number_ label="Reliability pause below" suffix="%" testId="input-reliability-pause"
              value={draft.reliability_pause_below_pct}
              onChange={(v) => setDraft({ ...draft, reliability_pause_below_pct: v })}
              min={0} max={100} step={1}/>
          </div>
        </section>

        {/* Menu & referrals */}
        <section className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-6 mb-4">
          <h2 className="font-black mb-4">Feast mode & referral rewards</h2>          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Select_ label="Default feast mode" testId="input-feast-mode"
              value={draft.default_feast_select_mode}
              options={[
                { value: "per_event",     label: "Per event (venue picks)" },
                { value: "member_picks",  label: "Member pre-selects" },
                { value: "surprise_menu", label: "Surprise Menu is the ethos" },
              ]}
              onChange={(v) => setDraft({ ...draft, default_feast_select_mode: v })}
            />
            <Number_ label="Referrer credit" suffix="¢" testId="input-referrer-credit"
              value={draft.referrer_credit_cents}
              onChange={(v) => setDraft({ ...draft, referrer_credit_cents: v })}
              min={0} max={100000} step={100}/>
            <Number_ label="Referee credit" suffix="¢" testId="input-referee-credit"
              value={draft.referee_credit_cents}
              onChange={(v) => setDraft({ ...draft, referee_credit_cents: v })}
              min={0} max={100000} step={100}/>
          </div>
        </section>

        {/* Legal review — pass the T&C draft to your solicitor */}
        <section
          data-testid="tc-legal-review"
          className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-6 mb-4"
        >
          <div className="flex items-center justify-between gap-3">
            <h2 className="font-black">Terms & Conditions — legal review</h2>
            <a
              href="/admin/hustlemeet-matchmaker"
              data-testid="link-open-matchmaker-console"
              className="text-xs font-bold text-amber-500 inline-flex items-center gap-1 hover:underline"
            >
              <Sparkles size={12}/> Open Matchmaker console
            </a>
          </div>
          <p className="text-xs text-stone-500 mb-4">
            6-section draft covering non-transferable credit, estate forfeit, cancel windows,
            dietary manifest, the &quot;Surprise Menu is the ethos&quot; positioning, the 12% fee split,
            Matchmaker eligibility, reliability score and referral rewards. Download and email
            to your solicitor for redlining before surfacing at checkout.
          </p>
          <Button
            data-testid="btn-download-tc"
            variant="outline"
            className="rounded-full font-black"
            onClick={async () => {
              try {
                const r = await axios.get(
                  `${API}/admin/hustlemeet-settings/tc-draft`,
                  { headers: authHeader(), withCredentials: true, responseType: "blob" });
                const url = URL.createObjectURL(new Blob([r.data]));
                const a = document.createElement("a");
                a.href = url;
                a.download = "HustleMeet-TC-Amendments-DRAFT.md";
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
                toast.success("Draft downloaded. Attach to your solicitor's email.");
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Download failed");
              }
            }}
          >
            <FileDown size={14} className="mr-1"/> Download draft
          </Button>
        </section>

        {/* Actions */}
        <div className="sticky bottom-4 mt-6 flex gap-3 justify-end">
          <Button
            variant="outline"
            data-testid="btn-reset"
            disabled={!dirty || busy}
            onClick={() => setDraft(orig)}
            className="rounded-full font-black"
          >
            <RotateCcw size={14} className="mr-1"/> Reset
          </Button>
          <Button
            data-testid="btn-save-settings"
            disabled={!dirty || busy || !splitOK}
            onClick={save}
            className="rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black"
          >
            <Save size={14} className="mr-1"/>
            {busy ? "Saving…" : "Save dials"}
          </Button>
        </div>
      </div>

      <SuiteFooter />
    </div>
  );
}
